import { useEffect, useState } from 'react'
import { auth } from '@/core/firebase/client'

/**
 * Reads the active Firebase Auth user's custom claims to detect a redeemed
 * preview token. Custom claims `previewClubId` (string) and
 * `previewExpiresAt` (epoch ms, number) are set by the
 * `redeem_preview_token` Cloud Function.
 *
 * Re-evaluates on every auth-state change. Returns `active: false` when:
 * - No signed-in user
 * - Signed-in user has no preview claim (i.e. they're a real staff/admin)
 * - The claim's expiry is in the past
 *
 * Used by:
 *   - AppShell topbar banner (sticky read-only mode indicator)
 *   - ClubGate: pin currentClubId to the preview's clubId
 *   - RoleGate: bypass per-route role checks (preview sees every screen)
 *
 * Side effects while active:
 *   - Toggles `body.preview-mode` class — CSS gates greys-out every `.btn`
 *     (except `.preview-allowed`), `.booking-block`, and form fields.
 *   - Installs a capture-phase `submit` listener that preventDefaults form
 *     submission so keyboard Enter can't bypass the CSS gate.
 */
export interface PreviewSessionState {
  /**
   * True until the very first onIdTokenChanged callback fires. Consumers
   * that gate UI on `active` (e.g. ClubGate's membership-skip path) MUST
   * also check `isLoading` — otherwise on first paint we'd race past the
   * gate before claims are read and end up on /no-club-access.
   */
  isLoading: boolean
  active: boolean
  isPublicDemo: boolean
  clubId: string | null
  expiresAt: Date | null
  daysLeft: number | null
}

const INITIAL: PreviewSessionState = {
  isLoading: true,
  active: false,
  isPublicDemo: false,
  clubId: null,
  expiresAt: null,
  daysLeft: null,
}

export function usePreviewSession(): PreviewSessionState {
  const [state, setState] = useState<PreviewSessionState>(INITIAL)

  useEffect(() => {
    let cancelled = false
    // Capture-phase submit interceptor — CSS `pointer-events:none` doesn't
    // block keyboard Enter on a form, so we have to preventDefault at the
    // document level. `.preview-allowed` opt-out keeps the claim card's
    // submit + the welcome dialog CTAs working. Listener is registered /
    // removed in lockstep with the `preview-mode` body class.
    const onSubmit = (e: SubmitEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('.preview-allowed')) return
      e.preventDefault()
      e.stopPropagation()
    }
    let listenerActive = false
    const apply = (next: PreviewSessionState) => {
      if (cancelled) return
      setState(next)
      document.body.classList.toggle('preview-mode', next.active)
      if (next.active && !listenerActive) {
        document.addEventListener('submit', onSubmit, true)
        listenerActive = true
      } else if (!next.active && listenerActive) {
        document.removeEventListener('submit', onSubmit, true)
        listenerActive = false
      }
    }
    const unsub = auth.onIdTokenChanged(async (user) => {
      if (cancelled) return
      if (!user) {
        apply({ ...INITIAL, isLoading: false })
        return
      }
      try {
        const tokenResult = await user.getIdTokenResult()
        const claims = tokenResult.claims as {
          previewClubId?: string
          previewExpiresAt?: number
          publicDemo?: boolean
        }
        const clubId = typeof claims.previewClubId === 'string' ? claims.previewClubId : null
        const expiresMs =
          typeof claims.previewExpiresAt === 'number' ? claims.previewExpiresAt : null
        if (!clubId || !expiresMs) {
          apply({ ...INITIAL, isLoading: false })
          return
        }
        const expiresAt = new Date(expiresMs)
        const daysLeft = Math.max(0, Math.ceil((expiresMs - Date.now()) / 86_400_000))
        const active = expiresMs > Date.now()
        apply({
          isLoading: false,
          active,
          isPublicDemo: active && claims.publicDemo === true,
          clubId: active ? clubId : null,
          expiresAt,
          daysLeft,
        })
      } catch {
        apply({ ...INITIAL, isLoading: false })
      }
    })
    return () => {
      cancelled = true
      document.body.classList.remove('preview-mode')
      if (listenerActive) {
        document.removeEventListener('submit', onSubmit, true)
        listenerActive = false
      }
      unsub()
    }
  }, [])

  return state
}
